import React, { useState, useEffect, useContext, createContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';

import { OrganizationContext } from '../../components/organization-select/withOrganization';

import eventsQuery from '../reports/custom-event-filter-modal/customEvents.gql';

import { FilterConstants } from './FilterConstants';

export const FiltersContext = createContext();

export function withFilters(Component) {
  return (props) => {
    const { selectedMembership } = useContext(OrganizationContext);

    const [ events, setEvents ] = useState([]);

    const [ currencyAndPermissionFilteredEvents, setCurrencyAndPermissionFilteredEvents ] = useState([]);

    const allUserSelectableEventIds = currencyAndPermissionFilteredEvents.map(event => event.id)
    const [ userSelectedEventIds, setUserSelectedEventIds ] = useState(allUserSelectableEventIds);
    
    const [ currencies, setCurrencies ] = useState([]);

    const [ nextTimeslot, setNextTimeslot ] = useState({});

    const [ dataError, setDataError ] = useState(null);
    const [ dataLoading, setDataLoading ] = useState(true);
    const [ isEmpty, setIsEmpty ] = useState(false);

    const hostId = selectedMembership.owner.id;

    const fetchMoreEvents = (totalCount, limit, eventsList, fetchMore) => {
      if (eventsList.length === totalCount) {
        return;
      };

      const offset = eventsList.length;
      fetchMore({
        variables: { offset, limit },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            ...prev,
            host: {
              ...prev.host,
              events: {
                ...prev.host.events,
                nodes: [
                  ...prev.host.events.nodes,
                  ...fetchMoreResult.host.events.nodes
                ]
              },
            },
          };
        }
      });
    };

    const { data, loading, error, fetchMore, refetch } = useQuery(eventsQuery, {
      variables: { hostId, limit: FilterConstants.EVENTS_QUERY_LIMIT },
    });

    const [ selectedFilters, setSelectedFilters ] = useState({
      currency: '',
      eventIds: [],
      // purchase date = date of order
      minPurchaseDate: moment().subtract(30, 'day').startOf('day').utcOffset(0, true),
      maxPurchaseDate: moment().endOf('day').utcOffset(0, true),
      minAttendanceDate: '',
      maxAttendanceDate: '',
      exactAttendanceStartTimeSecondsOfDay: '', // Specific timeslot, start time.
      exactAttendanceEndTimeSecondsOfDay: '', // Specific timeslot, end time as calculated by duration.
      minAttendanceStartTimeSecondsOfDay: '', // Range of timeslots, start time.
      maxAttendanceStartTimeSecondsOfDay: '', // Range of timeslots, end time.
      dataType: FilterConstants.TICKETS_AND_ADD_ONS
    });

    // Refetch event data when selected membership changes.
    useEffect(() => {
      setIsEmpty(false);
      refetch({ hostId: selectedMembership.owner.id })
    }, [selectedMembership, refetch]);

    useEffect(() => {
      setDataError(error);
      setDataLoading(loading);
    }, [error, loading]);

    useEffect(() => {
      if (!loading && data) {
        const { eventCurrencies } = data.host;

        // Set events.
        const postedOrExpiredEvents = data.host.events.nodes.filter((event) => {
          return event.state === 'POSTED' || event.state === 'EXPIRED';
        });
        setEvents(postedOrExpiredEvents);

        // Set currencies.
        setCurrencies(eventCurrencies);
      }
    }, [loading, data]);

    // Apply a default currency when there is no selection or the selection's invalid.
    useEffect(() => {
      if (currencies.length > 0) {
        const defaultCurrency = currencies[0];

        if (!selectedFilters.currency || !currencies.includes(selectedFilters.currency)) {
          setSelectedFilters({
            ...selectedFilters,
            currency: defaultCurrency,
            eventIds: [] // Clear event ID filters when currency changes.
          });
        }
      }
    }, [currencies, selectedFilters]);

    useEffect(() => {
      if (!loading && data) {
        const { totalCount } = data.host.events;
        const eventsList = data.host.events.nodes;
        fetchMoreEvents(totalCount, FilterConstants.EVENTS_QUERY_LIMIT, eventsList, fetchMore);
      };
    }, [loading, data, fetchMore]);

    useEffect(() => {
      // Ignore changes until data is not loading.
      if (loading || !data) {
        return;
      }

      // Ignore changes until all host events have loaded.
      if (data
        && data.host
        && data.host.events
        && data.host.events.nodes.length !== data.host.events.totalCount) {
        return;
      };

      // Calculate the events filtered by currency and permission (for MUM users).
      const filteredEvents = events.filter((event) => {
        if (!selectedFilters.currency || !selectedMembership) return false;
        const currencyCode = selectedFilters.currency.substring(0, 3); // TODO: Trim?
        const hasPermission = selectedMembership.all_listings 
          || (selectedMembership.event_ids && selectedMembership.event_ids.includes(event.id))
        return event.currency === currencyCode && hasPermission;
      });

      // Set the list of events filtered by currency and permission.
      setCurrencyAndPermissionFilteredEvents(filteredEvents);

      // Apply the user selections to build a final list of filtered events.
      const finalFilteredEvents = filteredEvents.filter((event) => {
        return userSelectedEventIds.length === 0 || userSelectedEventIds.includes(event.id);
      });
      let finalFilteredEventIds = finalFilteredEvents.map(event => event.id);

      // For MUM users, we can only show data if there are valid events selected.
      setIsEmpty(finalFilteredEventIds.length === 0);

      // When selecting the current user in the organization drop-down, the current user still shows up
      // as a membership with all_listings=true. We don't want to include all listing IDs in that case
      // since the user implicitly has access to all their own listings, and some hosts have thousands.
      const isCurrentUser = selectedMembership && selectedMembership.is_current_user;
      if (isCurrentUser && userSelectedEventIds.length === 0) {
        finalFilteredEventIds = [];
      }

      // Early-out if the filtered event IDs haven't changed.
      if (JSON.stringify(finalFilteredEventIds) === JSON.stringify(selectedFilters.eventIds)) {
        return;
      }

      // Calculate the next upcoming timeslot.
      const upcomingDates = finalFilteredEvents
        .filter((event) => {
          const timeslot = event.nextFutureTimeSlot
          const nextTimeslotStamp = timeslot && timeslot.startStamp

          const currentStamp = moment.utc().unix()

          return nextTimeslotStamp && nextTimeslotStamp >= currentStamp
        }).map(event => event.nextFutureTimeSlot)

      if (upcomingDates.length > 0) {
        setNextTimeslot(upcomingDates[0]);
      } else {
        // TODO: If there are no upcoming dates, the Date of Attendance UI shows:
        // "Next timeslot - Invalid Date, Invalid Date - Invalid Date"
      }

      // Set the eventIds to filter for sending to Superset.
      setSelectedFilters({
        ...selectedFilters,
        eventIds: finalFilteredEventIds
      });
    }, [data, loading, events, userSelectedEventIds, selectedFilters, selectedMembership]);

    return (
      <FiltersContext.Provider value={{
        currencyAndPermissionFilteredEvents,
        events,
        nextTimeslot,

        currencies,

        dataError,
        dataLoading,
        isEmpty,

        selectedFilters, setSelectedFilters,
      
        userSelectedEventIds, setUserSelectedEventIds,
        allUserSelectableEventIds}}>
        <Component {...props} />
      </FiltersContext.Provider>
    )
  };
}
